import React from 'react';

import ImageBase from './ImageBase';
import { Icon } from '../Icon';
import { TIcon } from '../../types/TStyleVariables';

type TAvatar = {
  src: string;
  alt: string;
  size?: 's' | 'm' | 'l';
  variant?: 'fill' | 'fit';
  className?: string;
  placeholderIconName: TIcon;
};

function shouldRenderPlaceholder(src: string, placeholderIconName: string) {
  return !src && Boolean(placeholderIconName);
}

function getWidthHeight(size: TAvatar['size'] = 'm') {
  const sizes = {
    s: '32px',
    m: '48px',
    l: '64px',
  };

  return { width: sizes[size], height: sizes[size] };
}

function createAvatarClassName(
  shouldShowPlaceholderIcon: boolean,
  className: string | undefined,
  variant: TAvatar['variant'] = 'fill',
) {
  const placeholderClass = shouldShowPlaceholderIcon ? 'hz-Image--avatar-placeholder' : '';
  const variantClass = `hz-Image--avatar-${variant}`;
  return [placeholderClass, variantClass, className].join(' ').trim();
}

type TGetAvatarProps = {
  alt: string;
  size: TAvatar['size'];
  variant: TAvatar['variant'];
  className?: string;
  shouldShowPlaceholderIcon: boolean;
  rest: Record<string, unknown>;
};

function getAvatarProps({ alt, size, variant, className, shouldShowPlaceholderIcon, rest }: TGetAvatarProps) {
  const as: keyof React.JSX.IntrinsicElements = shouldShowPlaceholderIcon ? 'div' : 'img';

  return {
    ...rest,
    as,
    kind: 'avatar' as keyof typeof ImageBase,
    alt,
    className: createAvatarClassName(shouldShowPlaceholderIcon, className, variant),
    style: { ...getWidthHeight(size) },
  };
}

const Avatar = ({ src, alt, size = 'm', variant = 'fill', className, placeholderIconName, ...rest }: TAvatar) => {
  const shouldShowPlaceholderIcon = shouldRenderPlaceholder(src, placeholderIconName);
  const _props = getAvatarProps({
    shouldShowPlaceholderIcon,
    rest,
    alt,
    className,
    size,
    variant,
  });

  if (shouldShowPlaceholderIcon) {
    return (
      <ImageBase {..._props}>
        <Icon name={placeholderIconName} color="background-default" className="hz-Image--placeholder-icon" />
      </ImageBase>
    );
  }

  return <ImageBase {..._props} src={src} />;
};

export default Avatar;
